<template>
  <div v-if="loading" class="max-w-7xl mx-auto px-4 py-8">
    <ProductPageSkeleton />
  </div>

  <div v-else-if="product" class="max-w-7xl mx-auto px-4 py-8">
    <Breadcrumbs :items="breadcrumbs" />

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
      <!-- Gallery -->
      <div>
        <div
          ref="galleryWrapRef"
          class="relative aspect-[4/5] mb-4 overflow-hidden rounded-xl ring-1 ring-default"
        >
          <ClientOnly v-if="show360Viewer">
            <Product360Viewer
              :frames="spinFrameUrls"
              :alt="product.name"
              :autoplay="true"
            />
            <template #fallback>
              <AppImage
                :src="spinFrameUrls[0]"
                :alt="product.name"
                preset="product"
                fit="inside"
                width="800"
                height="1000"
                sizes="xs:100vw sm:100vw md:100vw lg:600px xl:600px"
                class="w-full h-full"
                fetchpriority="high"
                loading="eager"
                :placeholder="false"
              />
            </template>
          </ClientOnly>
          <ProductImageZoom v-else :enabled="storefrontImageZoom">
            <AppImage
              :key="galleryImages[activeImageIndex]?.id ?? activeImageIndex"
              :src="galleryImages[activeImageIndex]?.url || product.images?.[0]?.url"
              :alt="product.name"
              preset="product"
              fit="inside"
              width="800"
              height="1000"
              sizes="xs:100vw sm:100vw md:100vw lg:600px xl:600px"
              class="w-full h-full"
              fetchpriority="high"
              loading="eager"
              :placeholder="false"
            />
          </ProductImageZoom>
        </div>

        <div
          v-if="has360Spin || galleryImages.length"
          class="flex gap-2 overflow-x-auto"
        >
          <button
            v-if="has360Spin"
            type="button"
            class="product-gallery-thumb shrink-0 w-16 h-20 rounded-lg overflow-hidden ring-1 ring-default relative"
            :class="{ active: show360Viewer }"
            aria-label="Vizualizare 360 grade"
            :aria-pressed="show360Viewer"
            @click="select360View"
          >
            <AppImage
              :src="spinFrameUrls[0]"
              alt=""
              preset="thumbnail"
              fit="inside"
              width="64"
              height="80"
              sizes="64px"
              loading="lazy"
              class="w-full h-full"
            />
            <span
              class="absolute inset-x-0 bottom-0 bg-black/65 py-0.5 text-center text-[9px] font-semibold uppercase tracking-wide text-white"
            >
              360°
            </span>
          </button>

          <button
            v-for="(img, idx) in galleryImages"
            :key="img.id"
            type="button"
            class="product-gallery-thumb shrink-0 w-16 h-20 rounded-lg overflow-hidden ring-1 ring-default relative"
            :class="{ active: !show360Viewer && activeImageIndex === idx }"
            :aria-label="`Vezi imaginea ${idx + 1}`"
            :aria-pressed="!show360Viewer && activeImageIndex === idx"
            @click="selectGalleryImage(idx)"
          >
            <AppImage
              :src="img.url"
              alt=""
              preset="thumbnail"
              fit="inside"
              width="64"
              height="80"
              sizes="64px"
              loading="lazy"
              class="w-full h-full"
            />
          </button>
        </div>
      </div>

      <!-- Product Info -->
      <div>
        <p v-if="product.brand" class="text-sm text-muted uppercase tracking-wide mb-1">
          <NuxtLink
            v-if="product.brand.slug"
            :to="shopPath(`/branduri/${product.brand.slug}`)"
            class="hover:text-primary-600 transition-colors"
            @mouseenter="prefetchBrand"
            @focus="prefetchBrand"
          >
            {{ product.brand.name }}
          </NuxtLink>
          <span v-else>{{ product.brand.name }}</span>
        </p>
        <div class="flex items-start justify-between gap-3 mb-2">
          <h1 class="text-2xl md:text-3xl font-bold text-highlighted">{{ product.name }}</h1>
          <ClientOnly>
            <UButton
              v-if="isAdmin"
              label="Editează"
              icon="i-heroicons-pencil"
              variant="soft"
              color="neutral"
              size="sm"
              class="shrink-0"
              :to="shopPath(`/admin/produse/${product.id}`)"
            />
          </ClientOnly>
        </div>

        <div v-if="reviewsEnabled && product.ratingCount > 0" class="flex items-center gap-2 mb-4">
          <div class="flex">
            <UIcon
              v-for="i in 5"
              :key="i"
              :name="i <= Math.round(product.ratingAvg) ? 'i-heroicons-star-solid' : 'i-heroicons-star'"
              class="w-5 h-5"
              :class="i <= Math.round(product.ratingAvg) ? 'text-yellow-400' : 'text-dimmed'"
            />
          </div>
          <span class="text-sm text-muted">{{ product.ratingAvg }} ({{ product.ratingCount }} recenzii)</span>
        </div>

        <div class="mb-6">
          <div
            v-if="hasDiscount"
            class="inline-flex items-center gap-2 rounded-full bg-rose-50 dark:bg-rose-950/40 px-3 py-1 text-sm font-semibold text-rose-700 ring-1 ring-rose-200 dark:ring-rose-900 mb-3"
          >
            <UIcon name="i-heroicons-receipt-percent" class="h-4 w-4" />
            Preț promoțional
          </div>
          <p v-if="hasDiscount" class="text-lg text-dimmed line-through">{{ formatPrice(compareAtPrice) }}</p>
          <p class="text-3xl font-bold" :class="hasDiscount ? 'text-rose-600 dark:text-rose-400' : 'text-highlighted'">
            <span
              v-if="(isPackageProduct && !packageSelectionsComplete) || (isVariableProduct && !usesAttributePricing && !selectedVariant)"
              class="text-lg font-semibold text-muted mr-1"
            >de la</span>
            {{ formatPriceWithUnit(totalDisplayPrice, product.priceUnit) }}
          </p>
          <p v-if="usesAttributePricing && attributePriceDelta > 0" class="text-sm text-muted mt-1">
            Preț produs {{ formatPrice(productBasePrice) }} + atribute {{ formatPrice(attributePriceDelta) }}
          </p>
          <p v-if="customFieldsPriceDelta > 0" class="text-sm text-muted mt-1">
            Preț de bază {{ formatPrice(displayPrice) }} + opțiuni {{ formatPrice(customFieldsPriceDelta) }}
          </p>
          <p v-else-if="isPackageProduct && packageSelectionsComplete" class="text-sm text-muted mt-1">
            Preț pachet pentru selecția ta
          </p>
          <p v-if="hasPriceUnit" class="text-sm text-muted mt-1">
            Total pentru {{ formatQuantityWithUnit(quantity, product.priceUnit) }}: {{ formatPrice(measuredTotal) }}
          </p>
          <p v-if="hasDiscount" class="text-sm font-medium text-emerald-600 dark:text-emerald-400 mt-1">
            Economisești {{ formatPrice(compareAtPrice - displayPrice) }}
          </p>
          <p class="text-sm text-muted mt-1">TVA inclus</p>
          <div
            v-if="productItemRolePricesVisible && listedItemRolePrices.length"
            class="mt-3 space-y-1.5 border-t border-default pt-3"
          >
            <p
              v-for="row in listedItemRolePrices"
              :key="row.role"
              class="flex items-baseline justify-between gap-3 text-sm"
            >
              <span class="text-muted">Preț {{ row.label }}</span>
              <span class="font-semibold text-highlighted">{{ formatPrice(row.price) }}</span>
            </p>
          </div>
        </div>

        <p v-if="product.shortDescription" class="text-toned mb-6">{{ product.shortDescription }}</p>

        <!-- Variants (variable products: visual attribute swatches) -->
        <div v-if="isVariableProduct && displayVariationAxes.length" class="space-y-4 mb-6">
          <div
            v-for="axis in displayVariationAxes"
            :key="axis.slug"
            class="space-y-1.5"
          >
            <div class="flex items-baseline justify-between gap-2">
              <p class="text-xs font-semibold uppercase tracking-wide text-muted">{{ axis.name }}</p>
              <p class="text-[11px] text-muted truncate max-w-[55%] text-right">
                {{ selectedOptions[axis.slug] }}
                <template v-if="selectedValueExtra(axis) > 0"> · +{{ formatPrice(selectedValueExtra(axis)) }}</template>
              </p>
            </div>

            <!-- Image swatches when values have photos (~half previous size) -->
            <div
              v-if="axisHasImages(axis)"
              class="grid grid-cols-7 sm:grid-cols-8 gap-1"
            >
              <button
                v-for="value in axis.values"
                :key="`${axis.slug}:${value.name}`"
                type="button"
                class="group relative overflow-hidden rounded-md border text-left transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                :class="selectedOptions[axis.slug] === value.name
                  ? 'border-primary-600 ring-1 ring-primary-600/30'
                  : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'"
                :title="valueExtra(value) > 0 ? `${value.name} +${formatPrice(valueExtra(value))}` : value.name"
                @click="selectVariationValue(axis.slug, value.name)"
              >
                <div class="aspect-square bg-gray-50 dark:bg-gray-900 overflow-hidden">
                  <AppImage
                    v-if="value.imageUrl"
                    :src="value.imageUrl"
                    :alt="value.name"
                    class="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                  />
                  <div
                    v-else
                    class="flex h-full w-full items-center justify-center text-[8px] text-muted px-0.5 text-center leading-tight"
                  >
                    {{ value.name }}
                  </div>
                </div>
                <span
                  v-if="selectedOptions[axis.slug] === value.name"
                  class="absolute top-0.5 right-0.5 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-primary-600 text-white shadow"
                >
                  <UIcon name="i-heroicons-check" class="h-1.5 w-1.5" />
                </span>
              </button>
            </div>

            <!-- Text chips when no images -->
            <div v-else class="flex flex-wrap gap-1.5">
              <button
                v-for="value in axis.values"
                :key="`${axis.slug}:${value.name}`"
                type="button"
                class="rounded-full border px-2.5 py-1 text-xs transition-colors"
                :class="selectedOptions[axis.slug] === value.name
                  ? 'border-primary-600 bg-primary-600 text-white'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'"
                @click="selectVariationValue(axis.slug, value.name)"
              >
                {{ value.name }}<template v-if="valueExtra(value) > 0"> +{{ formatPrice(valueExtra(value)) }}</template>
              </button>
            </div>
          </div>
        </div>

        <!-- Legacy phone variants (color / storage) -->
        <div v-else-if="product.variants?.length && !isVariableProduct" class="space-y-4 mb-6">
          <UFormField v-if="availableColors.length" :label="variantColorLabel">
            <div class="flex flex-wrap gap-2">
              <UButton
                v-for="color in availableColors"
                :key="color"
                :label="color"
                :variant="selectedColor === color ? 'solid' : 'soft'"
                size="sm"
                @click="selectedColor = color"
              />
            </div>
          </UFormField>
          <UFormField v-if="availableStorages.length" :label="variantStorageLabel">
            <div class="flex flex-wrap gap-2">
              <UButton
                v-for="storage in availableStorages"
                :key="storage"
                :label="storage"
                :variant="selectedStorage === storage ? 'solid' : 'soft'"
                size="sm"
                @click="selectedStorage = storage"
              />
            </div>
          </UFormField>
        </div>

        <div class="flex items-center gap-2 mb-6">
          <UBadge
            v-if="productItemRoleEnabled"
            color="neutral"
            variant="subtle"
          >
            {{ productItemRoleLabel }}
          </UBadge>
          <UBadge
            v-if="limitsStock"
            :color="product.stock > 0 ? 'success' : 'error'"
            variant="subtle"
          >
            {{ product.stock > 0 ? `În stoc (${product.stock})` : 'Stoc epuizat' }}
          </UBadge>
          <span v-if="product.sku" class="text-sm text-muted">SKU: {{ product.sku }}</span>
        </div>

        <ProductComplexFields
          v-if="complexAssignments.length"
          ref="complexFieldsRef"
          :assignments="complexAssignments"
          :base-price="displayPrice"
          @change="onComplexFieldsChange"
        />

        <ProductPackageFields
          v-if="isPackageProduct"
          ref="packageFieldsRef"
          :items="packageItems"
          :choice-groups="packageChoiceGroups"
          :allow-multiples="product.packageAllowMultiples === true"
          @change="onPackageFieldsChange"
        />

        <div ref="buyBoxRef" class="mb-8 space-y-3">
          <div class="flex flex-wrap items-center gap-3">
            <UInputNumber
              v-model="quantity"
              :min="catalogMinQty ?? (hasPriceUnit ? 0.01 : 1)"
              :step="hasPriceUnit ? 0.01 : 1"
              :max="limitsStock ? product.stock : undefined"
              class="w-28"
            />
            <span v-if="hasPriceUnit" class="text-sm text-muted">{{ product.priceUnit }}</span>
            <UButton
              :icon="inWishlist ? 'i-heroicons-heart-solid' : 'i-heroicons-heart'"
              color="primary"
              variant="ghost"
              size="lg"
              aria-label="Adaugă la favorite"
              @click="wishlistStore.toggleWishlist(product)"
            />
            <UButton
              icon="i-heroicons-scale"
              color="primary"
              variant="ghost"
              size="lg"
              aria-label="Compară"
              @click="cartStore.toggleCompare(product.id)"
            />
            <ProductCtaButtons
              v-if="productCtaButtons.length"
              :buttons="productCtaButtons"
              inline
            />
          </div>
          <UButton
            v-if="catalogExternalUrl"
            label="Vezi oferta"
            icon="i-heroicons-arrow-top-right-on-square"
            color="primary"
            variant="solid"
            size="lg"
            block
            class="w-full justify-center"
            :to="catalogExternalUrl"
            target="_blank"
            external
          />
          <UButton
            v-else
            label="Adaugă în coș"
            icon="i-heroicons-shopping-cart"
            color="primary"
            variant="solid"
            size="lg"
            block
            class="w-full justify-center"
            data-cy="add-to-cart"
            :disabled="limitsStock && product.stock <= 0"
            @click="handleAddToCart"
          />
          <TbiInstalmentHint :amount="hasPriceUnit ? measuredTotal : totalDisplayPrice" />
        </div>

        <CatalogModulesProduct
          v-if="product"
          :product-id="product.id"
          zone="buy"
          @ready="onCatalogReady"
        />

        <!-- Specifications -->
        <UCard v-if="visibleSpecifications.length">
          <template #header>
            <h2 class="font-semibold">Specificații tehnice</h2>
          </template>
          <dl class="divide-y divide-gray-100">
            <div
              v-for="row in visibleSpecifications"
              :key="row.key"
              class="flex items-center justify-between gap-3 py-2.5 text-sm"
            >
              <dt class="flex min-w-0 items-center gap-2.5 text-muted">
                <img
                  v-if="row.imageUrl"
                  :src="resolveMediaUrl(row.imageUrl, apiOrigin)"
                  alt=""
                  class="h-8 w-8 shrink-0 object-contain"
                >
                <span class="capitalize">{{ row.key }}</span>
              </dt>
              <dd class="shrink-0 font-medium text-highlighted">{{ row.value }}</dd>
            </div>
          </dl>
        </UCard>
      </div>
    </div>

    <!-- Description -->
    <section v-if="product.description" class="mt-12">
      <h2 class="text-xl font-bold text-highlighted mb-4">Descriere</h2>
      <div class="prose-cms max-w-none" v-html="descriptionHtml" />
    </section>

    <CatalogModulesProduct
      v-if="product"
      :product-id="product.id"
      zone="after"
    />

    <section v-if="videoEmbed" class="mt-12">
      <h2 class="text-xl font-bold text-highlighted mb-4">Video</h2>
      <div class="aspect-video w-full max-w-4xl overflow-hidden rounded-xl bg-elevated">
        <iframe
          :src="videoEmbed.embedUrl"
          :title="`Video ${product.name}`"
          class="h-full w-full"
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowfullscreen
          referrerpolicy="strict-origin-when-cross-origin"
        />
      </div>
    </section>

    <!-- Reviews -->
    <section v-if="reviewsEnabled" class="mt-12">
      <h2 class="text-xl font-bold text-highlighted mb-6">Recenzii ({{ product.ratingCount }})</h2>

      <UCard class="mb-6">
        <template #header>
          <h3 class="font-semibold">Lasă o recenzie</h3>
        </template>

        <div v-if="!isAuthenticated" class="text-sm text-toned">
          <NuxtLink
            :to="shopPath(`/cont/login?redirect=${encodeURIComponent(route.fullPath)}`)"
            class="text-primary-600 hover:underline font-medium"
          >
            Autentifică-te
          </NuxtLink>
          pentru a lăsa o recenzie.
        </div>

        <form v-else class="space-y-4" @submit.prevent="submitReview">
          <UFormField label="Rating" required>
            <div class="flex items-center gap-1">
              <button
                v-for="i in 5"
                :key="i"
                type="button"
                class="p-0.5"
                :aria-label="`${i} stele`"
                @click="reviewForm.rating = i"
              >
                <UIcon
                  :name="i <= reviewForm.rating ? 'i-heroicons-star-solid' : 'i-heroicons-star'"
                  class="w-6 h-6"
                  :class="i <= reviewForm.rating ? 'text-yellow-400' : 'text-dimmed'"
                />
              </button>
            </div>
          </UFormField>
          <UFormField label="Titlu">
            <UInput v-model="reviewForm.title" placeholder="Rezumat scurt (opțional)" maxlength="255" />
          </UFormField>
          <UFormField label="Comentariu">
            <UTextarea v-model="reviewForm.comment" :rows="3" placeholder="Spune-ne părerea ta despre produs" />
          </UFormField>
          <UButton type="submit" label="Trimite recenzia" color="primary" :loading="reviewSubmitting" />
          <p class="text-xs text-muted">Recenzia va apărea după aprobare.</p>
        </form>
      </UCard>

      <div v-if="product.reviews?.length" class="space-y-4">
        <UCard v-for="review in product.reviews" :key="review.id">
          <div class="flex items-center gap-2 mb-2">
            <div class="flex">
              <UIcon
                v-for="i in 5"
                :key="i"
                :name="i <= review.rating ? 'i-heroicons-star-solid' : 'i-heroicons-star'"
                class="w-4 h-4"
                :class="i <= review.rating ? 'text-yellow-400' : 'text-dimmed'"
              />
            </div>
            <span class="font-medium text-sm">{{ reviewDisplayName(review) }}</span>
            <span v-if="review.verifiedPurchase" class="text-xs font-medium text-emerald-700">Cumpărare verificată</span>
            <span class="text-xs text-dimmed">{{ new Date(review.reviewDate || review.createdAt).toLocaleDateString('ro-RO') }}</span>
          </div>
          <h4 v-if="review.title" class="font-medium mb-1">{{ review.title }}</h4>
          <p class="text-toned text-sm">{{ review.comment }}</p>
        </UCard>
      </div>
      <p v-else class="text-muted">Nu există recenzii pentru acest produs.</p>
    </section>

    <!-- Related Products -->
    <template v-for="relationType in PRODUCT_RELATION_TYPES" :key="relationType">
      <section
        v-if="product[PRODUCT_RELATION_SECTIONS[relationType].productField]?.length"
        class="mt-12"
      >
        <h2 class="text-xl font-bold text-highlighted mb-6">
          {{ productRelationTitle(relationType, settings) }}
        </h2>
        <ProductGrid :products="product[PRODUCT_RELATION_SECTIONS[relationType].productField]!" />
      </section>
    </template>

    <RecentlyViewedProducts :exclude-product-id="product.id" />
  </div>

  <div v-else-if="!loading" class="max-w-7xl mx-auto px-4 py-16 text-center">
    <h1 class="text-2xl font-bold text-highlighted mb-4">Produs negăsit</h1>
    <UButton label="Înapoi la produse" :to="shopPath('/produse')" />
  </div>

  <!-- Sticky mobile add-to-cart -->
  <Transition name="sticky-buy">
    <div
      v-if="product && showStickyBuy"
      class="fixed inset-x-0 bottom-0 z-40 border-t border-default bg-default/95 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] backdrop-blur-md lg:hidden"
      style="padding-bottom: max(0.75rem, env(safe-area-inset-bottom))"
    >
      <div class="mx-auto flex max-w-7xl flex-col gap-2">
        <div class="flex items-center gap-3">
          <div class="min-w-0 flex-1">
            <p class="truncate text-xs text-muted">{{ product.name }}</p>
            <p
              class="text-lg font-bold leading-tight"
              :class="hasDiscount ? 'text-rose-600 dark:text-rose-400' : 'text-highlighted'"
            >
              {{ formatPriceWithUnit(hasPriceUnit ? measuredTotal : totalDisplayPrice, hasPriceUnit ? null : product.priceUnit) }}
            </p>
          </div>
          <UButton
            v-if="catalogExternalUrl"
            label="Vezi oferta"
            icon="i-heroicons-arrow-top-right-on-square"
            color="primary"
            variant="solid"
            size="lg"
            class="shrink-0"
            :to="catalogExternalUrl"
            target="_blank"
            external
          />
          <UButton
            v-else
            label="Adaugă în coș"
            icon="i-heroicons-shopping-cart"
            color="primary"
            variant="solid"
            size="lg"
            class="shrink-0"
            :disabled="limitsStock && product.stock <= 0"
            @click="handleAddToCart"
          />
        </div>
        <TbiInstalmentHint
          compact
          :amount="hasPriceUnit ? measuredTotal : totalDisplayPrice"
        />
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import type { ApiResponse, Product, ProductCtaButton, ProductVariant } from '~/types'
import { resolveAbsoluteMediaUrl, resolveMediaUrl } from '~/utils/mediaUrl'
import { enrichHtmlMediaUrls } from '~/utils/htmlMedia'
import { parseProductSpecifications } from '~/utils/specifications'
import type { CustomFieldValueInput, ProductComplexFieldAssignment } from '~/utils/complexProductFields'
import {
  buildPackageSnapshots,
  computePackageFromPrice,
  packageSelectionsSatisfyRules,
  productIsComplex,
  productIsPackage,
  type ProductPackageChoiceGroup,
  type ProductPackageItem,
} from '~/utils/productPackage'
import {
  optionsMatch,
  productIsVariable,
  productUsesAttributePricing,
  sumAttributeExtras,
} from '~/utils/productVariants'
import { effectiveItemRole, listedRolePrices, PRODUCT_ITEM_ROLE_LABELS, STOREFRONT_ROLE_PRICE_KEYS } from '~/utils/productItemRole'
import { formatPriceWithUnit } from '~/utils/productCard'
import { formatQuantityWithUnit, productHasPriceUnit } from '~/utils/shopServices'
import { PRODUCT_RELATION_SECTIONS, PRODUCT_RELATION_TYPES, productRelationTitle } from '~/utils/productRelations'
import { productVideoEmbed } from '~/utils/productVideo'
const shopPath = useShopPath()

const route = useRoute()
const config = useRuntimeConfig()
const productsStore = useProductsStore()
const wishlistStore = useWishlistStore()
const cartStore = useCartStore()
const { addToCart } = useCart()
const { isAuthenticated, isAdmin } = useAuth()
const { post } = useApi()
const toast = useToast()
const { trackViewItem } = useGtmEcommerce()
const { trackViewItem: trackMetaViewItem } = useMetaEcommerce()
const { recordView } = useRecentlyViewed()
const { prefetchRoute } = useRoutePrefetch()

const {
  settings,
  site,
  branding,
  stockManagementEnabled,
  productItemRoleEnabled,
  productItemRolePricesVisible,
  shopServicesEnabled,
  reviewsEnabled,
  storefrontImageZoom,
} = useSiteSettings()
const galleryWrapRef = ref<HTMLElement | null>(null)
const storefrontRoot = useShopStorefrontRoot()
const shopPublicUrl = useShopPublicUrl()
const apiOrigin = config.public.apiOrigin as string
const shopApiBase = useShopApiBase()
const siteName = computed(() => site.value.name)
const trackedViewId = ref<number | null>(null)

const slug = computed(() => route.params.slug as string)

const reviewForm = reactive({
  rating: 5,
  title: '',
  comment: '',
})
const reviewSubmitting = ref(false)

async function submitReview() {
  if (!product.value) return
  if (reviewForm.rating < 1 || reviewForm.rating > 5) {
    toast.add({ title: 'Alege un rating între 1 și 5', color: 'error' })
    return
  }

  reviewSubmitting.value = true
  try {
    const response = await post('/reviews', {
      productId: product.value.id,
      rating: reviewForm.rating,
      title: reviewForm.title.trim() || undefined,
      comment: reviewForm.comment.trim() || undefined,
    })
    if (response.success) {
      toast.add({
        title: 'Recenzie trimisă',
        description: 'Va apărea după aprobare.',
        color: 'success',
      })
      reviewForm.rating = 5
      reviewForm.title = ''
      reviewForm.comment = ''
    }
  } catch (err: unknown) {
    const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
      || (err instanceof Error ? err.message : 'Nu am putut trimite recenzia')
    toast.add({ title: message, color: 'error' })
  } finally {
    reviewSubmitting.value = false
  }
}

const { data: product, pending: loading } = await useAsyncData(
  () => `product-${slug.value}`,
  async () => {
    const response = await $fetch<ApiResponse<Product>>(
      `${shopApiBase.value}/products/${slug.value}`,
    )
    if (!response.success || !response.data) return null
    return {
      ...response.data,
      specifications: parseProductSpecifications(response.data.specifications) ?? undefined,
    }
  },
  { watch: [slug] },
)

const { data: productCtaButtonsData } = await useAsyncData(
  'product-cta-buttons',
  async () => {
    try {
      const response = await $fetch<ApiResponse<ProductCtaButton[]>>(
        `${shopApiBase.value}/cta-buttons`,
      )
      if (!response.success || !response.data) return []
      return response.data
    } catch {
      return []
    }
  },
)

const productCtaButtons = computed(() => productCtaButtonsData.value || [])

const videoEmbed = computed(() => productVideoEmbed(product.value?.videoUrl))
const descriptionHtml = computed(() =>
  enrichHtmlMediaUrls(product.value?.description || '', apiOrigin),
)

watch(product, (value) => {
  productsStore.currentProduct = value
}, { immediate: true })

const activeImageIndex = ref(0)
const show360Viewer = ref(false)
const quantity = ref(1)
const catalogExternalUrl = ref('')
const catalogMinQty = ref<number | null>(null)

function onCatalogReady(payload: { externalUrl: string | null, minQty: number | null }) {
  catalogExternalUrl.value = payload.externalUrl || ''
  catalogMinQty.value = payload.minQty
}

function reviewDisplayName(review: { authorName?: string | null, user?: { firstName?: string, lastName?: string } }) {
  const author = review.authorName?.trim()
  if (author) return author
  const first = review.user?.firstName || ''
  const last = review.user?.lastName?.[0]
  return last ? `${first} ${last}.` : first
}
const selectedColor = ref<string>()
const selectedStorage = ref<string>()
const selectedOptions = reactive<Record<string, string>>({})

const INTERNAL_SPEC_KEYS = new Set([
  'giftster',
  'sourceDesign',
  'variantOptionLabels',
  'aiCategory',
  'giftsterTipImagePrefixes',
  'giftsterGalleryGroups',
  'giftsterStorefrontOrder',
  'giftsterDesignSvgs',
])

const isVariableProduct = computed(() => productIsVariable(product.value))
const usesAttributePricing = computed(() =>
  productUsesAttributePricing(product.value?.variationAttributes),
)

const variationAxes = computed(() => {
  const axes = product.value?.variationAttributes
  if (Array.isArray(axes) && axes.length) return axes
  // Infer axes from variant.options when config missing (older rows)
  if (!isVariableProduct.value || !product.value?.variants?.length) return []
  const slugs = new Set<string>()
  for (const v of product.value.variants) {
    if (v.options) Object.keys(v.options).forEach((k) => slugs.add(k))
  }
  return [...slugs].map((slug) => {
    const values = [...new Set(
      (product.value?.variants || [])
        .map((v) => v.options?.[slug])
        .filter(Boolean) as string[],
    )].map((name, idx) => ({ id: idx + 1, name }))
    return {
      attributeId: 0,
      slug,
      name: slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
      values,
    }
  })
})

/** Cascade: only show values that exist in active variants given other selections. */
const displayVariationAxes = computed(() => {
  if (usesAttributePricing.value) return variationAxes.value
  const variants = (product.value?.variants || []).filter((v) => v.isActive !== false)
  return variationAxes.value.map((axis) => {
    const other: Record<string, string> = {}
    for (const [slug, name] of Object.entries(selectedOptions)) {
      if (slug !== axis.slug && name) other[slug] = name
    }
    const values = axis.values.filter((value) =>
      variants.some((v) => {
        if (v.options?.[axis.slug] !== value.name) return false
        return Object.entries(other).every(([k, val]) => v.options?.[k] === val)
      }),
    )
    return { ...axis, values: values.length ? values : axis.values }
  })
})

const variantOptionLabels = computed(() => {
  const specs = product.value?.specifications as Record<string, unknown> | undefined
  const raw = specs?.variantOptionLabels
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const labels = raw as Record<string, unknown>
    return {
      color: typeof labels.color === 'string' ? labels.color : null,
      storage: typeof labels.storage === 'string' ? labels.storage : null,
    }
  }
  if (specs?.giftster === true || specs?.giftster === 'true') {
    return { color: 'Tip produs', storage: 'Accesorii' }
  }
  return { color: null, storage: null }
})

const variantColorLabel = computed(() => variantOptionLabels.value.color || 'Culoare')
const variantStorageLabel = computed(() => variantOptionLabels.value.storage || 'Stocare')

const visibleSpecifications = computed(() => {
  const specs = product.value?.specifications
  if (!specs) return [] as Array<{ key: string; value: string; imageUrl: string | null }>

  const imageByName = new Map<string, string>()
  for (const assignment of product.value?.attributeAssignments || []) {
    const name = assignment.attribute?.name?.trim()
    const imageUrl = assignment.attribute?.imageUrl
    if (name && imageUrl) imageByName.set(name.toLocaleLowerCase('ro'), imageUrl)
  }

  return Object.entries(specs)
    .filter(([key, value]) => {
      if (key.startsWith('__') || INTERNAL_SPEC_KEYS.has(key)) return false
      if (value == null) return false
      if (typeof value === 'object') return false
      return String(value).trim().length > 0
    })
    .map(([key, value]) => ({
      key,
      value: String(value),
      imageUrl: imageByName.get(key.trim().toLocaleLowerCase('ro')) ?? null,
    }))
})

const complexFieldsRef = ref<{ validate: () => boolean; getValues: () => CustomFieldValueInput[] } | null>(null)
const packageFieldsRef = ref<{ validate: () => boolean; getSelectedIds: () => number[] } | null>(null)
const customFieldsPriceDelta = ref(0)
const pendingCustomFields = ref<CustomFieldValueInput[]>([])
const pendingPackageItemIds = ref<number[]>([])
const packageUnitPrice = ref(0)

const spinFrames = computed(() =>
  (product.value?.images || []).filter((img) => img.is360),
)
const spinFrameUrls = computed(() => spinFrames.value.map((img) => img.url))
const has360Spin = computed(() => spinFrames.value.length >= 2)
/** Static gallery: everything not part of the 360 sequence. */
const galleryImages = computed(() =>
  (product.value?.images || []).filter((img) => !img.is360),
)

function select360View() {
  show360Viewer.value = true
}

function selectGalleryImage(idx: number) {
  show360Viewer.value = false
  activeImageIndex.value = idx
}

watch(product, (value) => {
  activeImageIndex.value = 0
  show360Viewer.value = (value?.images || []).filter((img) => img.is360).length >= 2
  quantity.value = 1
  selectedColor.value = value?.color || undefined
  selectedStorage.value = value?.storage || undefined
  for (const key of Object.keys(selectedOptions)) delete selectedOptions[key]
  customFieldsPriceDelta.value = 0
  pendingCustomFields.value = []
  pendingPackageItemIds.value = []
  packageUnitPrice.value = 0
}, { immediate: true })

watch(displayVariationAxes, (axes) => {
  for (const axis of axes) {
    const current = selectedOptions[axis.slug]
    const stillValid = current && axis.values.some((v) => v.name === current)
    if (!stillValid && axis.values[0]) {
      selectedOptions[axis.slug] = axis.values[0].name
    }
  }
}, { immediate: true, deep: true })

function axisHasImages(axis: { values: Array<{ imageUrl?: string | null }> }) {
  return axis.values.some((v) => Boolean(v.imageUrl))
}

function valueExtra(value: { extraPrice?: number | null }) {
  const amount = Number(value.extraPrice)
  return Number.isFinite(amount) && amount > 0 ? amount : 0
}

function selectedValueExtra(axis: { slug: string; values: Array<{ name: string; extraPrice?: number | null }> }) {
  const selected = selectedOptions[axis.slug]
  const value = axis.values.find((row) => row.name === selected)
  return value ? valueExtra(value) : 0
}

function selectVariationValue(slug: string, name: string) {
  selectedOptions[slug] = name
  // Cascade: clear dependent axes that no longer match a real variant
  for (const axis of displayVariationAxes.value) {
    if (axis.slug === slug) continue
    const current = selectedOptions[axis.slug]
    if (current && !axis.values.some((v) => v.name === current) && axis.values[0]) {
      selectedOptions[axis.slug] = axis.values[0].name
    }
  }

  // General: jump gallery to first image linked to this attribute value
  const axis = variationAxes.value.find((a) => a.slug === slug)
  const value = axis?.values.find((v) => v.name === name)
  const valueId = value?.id != null ? Number(value.id) : null
  if (valueId != null && valueId > 0) {
    const linkedIdx = galleryImages.value.findIndex(
      (img) => Number(img.attributeValueId) === valueId,
    )
    if (linkedIdx >= 0) {
      show360Viewer.value = false
      activeImageIndex.value = linkedIdx
      return
    }
  }

  // Fallback: value swatch URL matches a gallery image
  if (value?.imageUrl) {
    const byUrl = galleryImages.value.findIndex((img) => img.url === value.imageUrl)
    if (byUrl >= 0) {
      show360Viewer.value = false
      activeImageIndex.value = byUrl
    }
  }
}

const isPackageProduct = computed(() => productIsPackage(product.value))
const isGeneratedProduct = computed(() => Number(product.value?.generatorId) > 0)
const limitsStock = computed(() =>
  stockManagementEnabled.value
  && !shopServicesEnabled.value
  && !isPackageProduct.value
  && !isGeneratedProduct.value,
)
const isComplexProduct = computed(() => productIsComplex(product.value))
const productItemRoleLabel = computed(() =>
  PRODUCT_ITEM_ROLE_LABELS[effectiveItemRole(product.value?.itemRole, productItemRoleEnabled.value)],
)

const listedItemRolePrices = computed(() =>
  productItemRolePricesVisible.value
    ? listedRolePrices(product.value?.rolePrices, STOREFRONT_ROLE_PRICE_KEYS)
    : [],
)

const packageItems = computed<ProductPackageItem[]>(() =>
  isPackageProduct.value ? (product.value?.packageItems || []) : [],
)

const packageChoiceGroups = computed<ProductPackageChoiceGroup[]>(() =>
  isPackageProduct.value ? (product.value?.packageChoiceGroups || []) : [],
)

const packageSelectionsComplete = computed(() =>
  isPackageProduct.value
  && packageItems.value.length > 0
  && packageSelectionsSatisfyRules(
    packageItems.value,
    pendingPackageItemIds.value,
    undefined,
    packageChoiceGroups.value,
    product.value?.packageAllowMultiples === true,
  ),
)

const complexAssignments = computed<ProductComplexFieldAssignment[]>(() => {
  if (!isComplexProduct.value) return []
  return (product.value?.complexFieldAssignments || [])
    .filter((row) => row.field?.isActive !== false && row.field?.hideFromCustomer !== true)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
})

const productBasePrice = computed(() =>
  Number(product.value?.salePrice ?? product.value?.price ?? 0),
)

const attributePriceDelta = computed(() => {
  if (!usesAttributePricing.value) return 0
  return sumAttributeExtras(product.value?.variationAttributes, selectedOptions)
})

const compareAtPrice = computed(() => {
  const price = Number(product.value?.price ?? 0)
  if (usesAttributePricing.value) return price + attributePriceDelta.value
  return price
})

const displayPrice = computed(() => {
  if (isPackageProduct.value) {
    if (packageSelectionsComplete.value) return packageUnitPrice.value
    return computePackageFromPrice(
      packageItems.value,
      undefined,
      packageChoiceGroups.value,
      product.value?.packageAllowMultiples === true,
    )
  }
  if (usesAttributePricing.value) return productBasePrice.value + attributePriceDelta.value
  const variant = selectedVariant.value
  if (variant) return Number(variant.salePrice ?? variant.price ?? product.value!.price)
  return productBasePrice.value
})

const totalDisplayPrice = computed(() => {
  if (isPackageProduct.value) return displayPrice.value + customFieldsPriceDelta.value
  return displayPrice.value + customFieldsPriceDelta.value
})

const hasPriceUnit = computed(() => productHasPriceUnit(product.value))
const measuredTotal = computed(() => totalDisplayPrice.value * Number(quantity.value || 0))

function onComplexFieldsChange(values: CustomFieldValueInput[], priceDelta: number) {
  pendingCustomFields.value = values
  customFieldsPriceDelta.value = priceDelta
}

function onPackageFieldsChange(selectedIds: number[], unitPrice: number) {
  pendingPackageItemIds.value = selectedIds
  packageUnitPrice.value = unitPrice
}

const hasDiscount = computed(() => {
  if (!product.value) return false
  return product.value.salePrice && product.value.salePrice < product.value.price
})

const selectedVariant = computed((): ProductVariant | undefined => {
  if (!product.value?.variants?.length) return undefined
  if (isVariableProduct.value) {
    return product.value.variants.find((v) =>
      v.isActive !== false && optionsMatch(v.options, selectedOptions),
    )
  }
  return product.value.variants.find(v =>
    (!selectedColor.value || v.color === selectedColor.value)
    && (!selectedStorage.value || v.storage === selectedStorage.value),
  )
})

const availableColors = computed(() =>
  [...new Set(product.value?.variants?.map(v => v.color).filter(Boolean) as string[])],
)

const availableStorages = computed(() =>
  [...new Set(product.value?.variants?.map(v => v.storage).filter(Boolean) as string[])],
)

watch([availableColors, availableStorages, product], () => {
  if (!product.value || isVariableProduct.value) return
  if (!selectedColor.value) selectedColor.value = product.value.color || availableColors.value[0]
  if (!selectedStorage.value) selectedStorage.value = product.value.storage || availableStorages.value[0]
}, { immediate: true })

const inWishlist = computed(() => product.value ? wishlistStore.isInWishlist(product.value.id) : false)

const breadcrumbs = computed(() => {
  const items = [{ label: 'Produse', to: shopPath('/produse') }]
  if (product.value?.category) {
    items.push({ label: product.value.category.name, to: shopPath(`/categorii/${product.value.category.slug}`) })
  }
  if (product.value) {
    items.push({ label: product.value.name })
  }
  return items
})

function handleAddToCart() {
  if (!product.value) return
  if (isVariableProduct.value && !usesAttributePricing.value && !selectedVariant.value) {
    toast.add({
      title: 'Alege o combinație de variații',
      color: 'warning',
    })
    return
  }
  if (
    complexAssignments.value.length
    && complexFieldsRef.value
    && !complexFieldsRef.value.validate()
  ) {
    toast.add({
      title: 'Completează câmpurile obligatorii',
      color: 'warning',
    })
    return
  }
  if (isPackageProduct.value) {
    if (!packageFieldsRef.value?.validate()) {
      toast.add({
        title: 'Alege produsele din fiecare grup al pachetului',
        color: 'warning',
      })
      return
    }
    const packageItemProductIds = packageFieldsRef.value.getSelectedIds()
    addToCart(product.value, quantity.value, undefined, {
      customFields: pendingCustomFields.value,
      packageItemProductIds,
      packageUnitPrice: packageUnitPrice.value,
      packageSnapshots: buildPackageSnapshots(
        packageItems.value,
        packageItemProductIds,
        undefined,
        packageChoiceGroups.value,
        product.value.packageAllowMultiples === true,
      ),
      flyFrom: galleryWrapRef.value,
    })
    return
  }
  addToCart(
    product.value,
    quantity.value,
    usesAttributePricing.value ? undefined : selectedVariant.value,
    {
      customFields: pendingCustomFields.value,
      variationSelections: usesAttributePricing.value ? { ...selectedOptions } : undefined,
      flyFrom: galleryWrapRef.value,
    },
  )
}

function prefetchBrand() {
  const brandSlug = product.value?.brand?.slug
  if (brandSlug) prefetchRoute(shopPath(`/branduri/${brandSlug}`))
}

function buildProductBreadcrumbLdItems(value: Product) {
  const items = [{ name: 'Produse', url: shopPublicUrl('/produse') }]
  if (value.category) {
    items.push({
      name: value.category.name,
      url: shopPublicUrl(`/categorii/${value.category.slug}`),
    })
  }
  items.push({ name: value.name, url: shopPublicUrl(`/produse/${value.slug}`) })
  return items
}

const buyBoxRef = ref<HTMLElement | null>(null)
const buyBoxVisible = ref(true)
const showStickyBuy = computed(() => !buyBoxVisible.value)

useIntersectionObserver(buyBoxRef, ([entry]) => {
  buyBoxVisible.value = entry?.isIntersecting ?? true
})

const seoTitle = computed(() => {
  if (!product.value) return 'Produs negăsit'
  return product.value.metaTitle || product.value.name
})
const seoDescription = computed(() => {
  if (!product.value) return 'Produsul căutat nu a fost găsit.'
  return product.value.metaDescription || product.value.shortDescription || product.value.name
})
const seoImage = computed(() => {
  if (!product.value?.images?.[0]?.url) return branding.value.ogImage
  return resolveAbsoluteMediaUrl(product.value.images[0].url, storefrontRoot.value, apiOrigin)
})

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  ogTitle: () => `${seoTitle.value} | ${siteName.value}`,
  ogDescription: seoDescription,
  ogImage: seoImage,
  ogUrl: () => shopPublicUrl(`/produse/${slug.value}`),
  ogType: () => (product.value ? 'product' : 'website'),
  ogSiteName: siteName,
  ogLocale: 'ro_RO',
  twitterCard: 'summary_large_image',
  twitterTitle: () => `${seoTitle.value} | ${siteName.value}`,
  twitterDescription: seoDescription,
  twitterImage: seoImage,
  robots: () => (product.value ? 'index, follow' : 'noindex, nofollow'),
})

useHead({
  link: computed(() => [{ rel: 'canonical', href: shopPublicUrl(`/produse/${slug.value}`) }]),
  script: computed(() => {
    if (!product.value) return []
    return [{
      type: 'application/ld+json',
      innerHTML: JSON.stringify([
        productJsonLd(product.value, {
          siteUrl: storefrontRoot.value,
          siteName: siteName.value,
          apiOrigin,
          stockManaged: stockManagementEnabled.value,
          reviewsEnabled: reviewsEnabled.value,
        }),
        siteBreadcrumbJsonLd(buildProductBreadcrumbLdItems(product.value), storefrontRoot.value),
      ]),
    }]
  }),
})

watch(product, (value) => {
  if (!value) return

  recordView(value)
  if (trackedViewId.value !== value.id) {
    trackedViewId.value = value.id
    trackViewItem(value)
    trackMetaViewItem(value)
  }
}, { immediate: true })
</script>
